import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { BookOpen, Heart, MessageCircle, Plus, CheckCircle, Clock, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDistanceToNow } from '@/lib/utils'

export default function RecoveryStories() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [strugglePeriod, setStrugglePeriod] = useState('')
  const [recoveryPeriod, setRecoveryPeriod] = useState('')
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [expandedStoryId, setExpandedStoryId] = useState<string | null>(null)
  const [commentInputs, setCommentInputs] = useState<Record<string, string>>({})

  const { data: allStories } = useQuery({
    queryKey: ['recovery-stories'],
    queryFn: () => api.get('/recovery-stories').then(r => r.data)
  })

  const { data: myStories } = useQuery({
    queryKey: ['my-recovery-stories'],
    queryFn: () => api.get('/recovery-stories/my/stories').then(r => r.data),
    enabled: isAuthenticated
  })

  const createMutation = useMutation({
    mutationFn: () => api.post('/recovery-stories', {
      title, content, strugglePeriod, recoveryPeriod, isAnonymous
    }),
    onSuccess: () => {
      setShowForm(false)
      setTitle('')
      setContent('')
      setStrugglePeriod('')
      setRecoveryPeriod('')
      setIsAnonymous(false)
      queryClient.invalidateQueries({ queryKey: ['recovery-stories'] })
      queryClient.invalidateQueries({ queryKey: ['my-recovery-stories'] })
      toast({ title: t('recovery.storySent') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const likeMutation = useMutation({
    mutationFn: (id: string) => api.post(`/recovery-stories/${id}/like`).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recovery-stories'] })
      queryClient.invalidateQueries({ queryKey: ['my-recovery-stories'] })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const commentMutation = useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      api.post(`/recovery-stories/${id}/comments`, { content }).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recovery-stories'] })
      queryClient.invalidateQueries({ queryKey: ['my-recovery-stories'] })
      toast({ title: t('common.success') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const pendingStories = myStories?.filter((s: any) => !s.isVerified) || []
  const verifiedStories = allStories?.stories || []

  const toggleComments = (id: string) => {
    setExpandedStoryId(prev => (prev === id ? null : id))
  }

  const handleCommentChange = (id: string, value: string) => {
    setCommentInputs(prev => ({ ...prev, [id]: value }))
  }

  const handleSendComment = (id: string) => {
    const text = commentInputs[id]?.trim()
    if (!text) return
    commentMutation.mutate({ id, content: text }, {
      onSuccess: () => {
        setCommentInputs(prev => ({ ...prev, [id]: '' }))
      }
    })
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <BookOpen className="w-7 h-7 text-primary" />
        </div>
        <h1 className="font-display text-2xl font-bold">{t('recovery.title')}</h1>
        <p className="text-muted-foreground mt-2">{t('recovery.subtitle')}</p>
      </div>

      {isAuthenticated && (
        <Button type="button" onClick={() => setShowForm(!showForm)} className="w-full mb-6 rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> {t('recovery.shareStory')}
        </Button>
      )}

      {showForm && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-6 p-5 rounded-2xl bg-card border border-border space-y-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('recovery.titlePlaceholder')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50" />
          <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={t('recovery.contentPlaceholder')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none min-h-[150px]" />
          <input value={strugglePeriod} onChange={(e) => setStrugglePeriod(e.target.value)} placeholder={t('recovery.strugglePlaceholder')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm" />
          <input value={recoveryPeriod} onChange={(e) => setRecoveryPeriod(e.target.value)} placeholder={t('recovery.recoveryPlaceholder')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)} className="rounded" />
            {t('recovery.anonymous')}
          </label>
          <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending || !title || !content}>
            {createMutation.isPending ? t('recovery.sending') : t('recovery.send')}
          </Button>
        </motion.div>
      )}

      {/* My pending stories */}
      {isAuthenticated && pendingStories.length > 0 && (
        <div className="mb-6">
          <h2 className="font-medium text-sm text-muted-foreground mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" /> {t('recovery.pendingStories', { count: pendingStories.length })}
          </h2>
          <div className="space-y-3">
            {pendingStories.map((story: any) => (
              <motion.div key={story._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold">{story.title}</h3>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-500">{t('recovery.awaitingApproval')}</span>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">{story.content}</p>
                <p className="text-xs text-muted-foreground mt-2">{formatDistanceToNow(story.createdAt)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {verifiedStories.map((story: any) => (
          <motion.div key={story._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-5 rounded-2xl bg-card border border-border hover:border-primary/20 transition-colors">
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-lg">{story.title}</h3>
              {story.isVerified && <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{story.content}</p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>{story.username}</span>
              {isAuthenticated && (
                <button
                  onClick={() => likeMutation.mutate(story._id)}
                  disabled={likeMutation.isPending && likeMutation.variables === story._id}
                  className="flex items-center gap-1 hover:text-primary transition-colors disabled:opacity-50"
                  aria-label={t('recovery.likeStory')}
                  title={t('recovery.likeStory')}
                >
                  <Heart className={`w-3 h-3 ${story.liked ? 'fill-current text-red-500' : ''}`} />
                  <span>{story.likes || 0}</span>
                </button>
              )}
              <button
                type="button"
                aria-label={t('recovery.comments')}
                onClick={() => toggleComments(story._id)}
                className="flex items-center gap-1 hover:text-primary transition-colors"
              >
                <MessageCircle className="w-3 h-3" />
                <span>{story.comments?.length || 0}</span>
              </button>
              <span>{formatDistanceToNow(story.createdAt)}</span>
            </div>

            <AnimatePresence>
              {expandedStoryId === story._id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 pt-4 border-t border-border space-y-3">
                    {story.comments?.length > 0 && (
                      <div className="space-y-2">
                        {story.comments.map((comment: any) => (
                          <div key={comment._id || comment.id} className="text-sm">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-medium text-foreground">{comment.author?.username || comment.author || comment.username}</span>
                              <span className="text-xs text-muted-foreground">{formatDistanceToNow(comment.createdAt)}</span>
                            </div>
                            <p className="text-muted-foreground">{comment.content}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {isAuthenticated && (
                      <div className="flex items-center gap-2">
                        <input
                          value={commentInputs[story._id] || ''}
                          onChange={(e) => handleCommentChange(story._id, e.target.value)}
                          placeholder={t('recovery.writeComment')}
                          className="flex-1 px-3 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSendComment(story._id)
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleSendComment(story._id)}
                          disabled={commentMutation.isPending && commentMutation.variables?.id === story._id}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
        {(!verifiedStories || verifiedStories.length === 0) && (
          <div className="text-center py-12 text-muted-foreground">{t('recovery.noStories')}</div>
        )}
      </div>
    </div>
  )
}
